import { useCreation } from "ahooks";
import { useQueries } from "react-query";
import { User } from "types";
import { api } from "@/api/api";

// One request per id via /users/{id}: the /users collection (id[in]) returns travelers only, so a
// batched request leaves office/staff ids unresolved. Mirrors useResource<User>(["users", id]) so the
// caches align; useResource can't be called in a loop, hence this thin per-id copy.
export const useUsers = (userIds: string[]): Record<string, User> => {
    const results = useQueries(
        userIds.map((id) => ({
            queryKey: ["users", id],
            queryFn: async () => {
                const { data } = await api.get<User>(`/users/${id}`);
                return data;
            },
            staleTime: Number.POSITIVE_INFINITY,
            enabled: Boolean(id),
        })),
    );

    // Include dataUpdatedAt so a refetch that changes a user's fields but not its id rebuilds the map.
    const loadedKey = results.map((result) => `${result.data?.id ?? ""}:${result.dataUpdatedAt}`).join(",");
    return useCreation(() => {
        const map: Record<string, User> = {};
        for (const result of results) {
            if (result.data?.id) {
                map[result.data.id] = result.data;
            }
        }
        return map;
    }, [loadedKey]);
};
